import React, { useState, useEffect } from "react";
import {
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  Slide,
  IconButton,
  TextField,
  MenuItem,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";

import {
  nameValidation,
  descriptionValidation,
  endpoint,
  priceValidation,
  currencyISOCode,
} from "../../config";
import { customToast } from "../../lib/toastLib";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export default function PlanDialog(props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const currency = currencyISOCode;
  // props value
  const { open, handleClose } = props;

  // state logic start
  const initialState = {
    name: "",
    nameError: "",
    description: "",
    descriptionError: "",
    stripePriceId: "",
    stripeProductId: "",
    price: "",
    priceError: "",
    currencyIsoCode: "CAD",
    billingInterval: 1,
  };

  const [state, setState] = useState({ ...initialState });

  const handleNameChange = (e) => {
    const value = e.target.value;
    setState({
      ...state,
      name: value,
      nameError:
        value && !value.match(nameValidation.regex)
          ? nameValidation.message
          : "",
    });
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setState({
      ...state,
      description: value,
      descriptionError:
        value && !value.match(descriptionValidation.regex)
          ? descriptionValidation.message
          : "",
    });
  };

  const handleStripePriceIdChange = (e) => {
    const value = e.target.value;
    setState({ ...state, stripePriceId: value });
  };

  const handleStripeProductIdChange = (e) => {
    const value = e.target.value;
    setState({ ...state, stripeProductId: value });
  };

  const handlePriceChange = (e) => {
    const value = e.target.value;
    setState({
      ...state,
      price: value,
      priceError:
        value && !value.match(priceValidation.regex)
          ? priceValidation.message
          : "",
    });
  };

  const handleCurrencyIsoCodeChange = (e) => {
    const value = e.target.value;
    setState({ ...state, currencyIsoCode: value });
  };

  const handleBillingIntervalChange = (e) => {
    const value = e.target.value;
    if (value === "" || Number(value) < 0) {
      return;
    }
    setState({ ...state, billingInterval: Number(value) });
  };
  // state logic end

  // submit button disable state logic start
  const [isSubmitBtnDisbaled, setIsSubmitBtnDisbaled] = useState(true);

  useEffect(() => {
    setIsSubmitBtnDisbaled(
      (!state.name || state.nameError ? true : false) ||
      (!state.description || state.descriptionError ? true : false) ||
      !state.stripePriceId ||
      !state.stripeProductId ||
      (!state.price || state.priceError ? true : false) ||
      !state.currencyIsoCode
    );
  }, [state]);
  // submit button disable state logic end

  // handle submit logic start
  const [submitBtnLoading, setSubmitBtnLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const handleCatchLogic = (error) => {
      setSubmitBtnLoading(false);
      if (error.response) {
        customToast.error(error.response.data?.message);
      } else {
        customToast.error(error.message);
      }
    };

    const requestBody = {
      name: state.name,
      description: state.description,
      stripePriceId: state.stripePriceId,
      stripeProductId: state.stripeProductId,
      price: state.price,
      currecyIsoCode: state.currencyIsoCode,
      billingInterval: state.billingInterval,
    };
    setSubmitBtnLoading(true);

    axios
      .post(`${endpoint}/api/plan`, requestBody)
      .then(() => {
        setSubmitBtnLoading(false);
        customToast.success("Plan added successfully");
        handleClose(true);
      })
      .catch((error) => handleCatchLogic(error));
  };
  // handle submit logic end

  // reset & value assign logic start
  useEffect(() => {
    if (open) {
      setState({ ...initialState });
    }
  }, [open]);
  // reset & value assign logic end

  return (
    <Dialog
      open={open}
      scroll="paper"
      fullScreen={fullScreen}
      fullWidth
      TransitionComponent={Transition}
    >
      <form onSubmit={handleSubmit} autoComplete="off">
        <DialogContent>
          <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
            <IconButton onClick={() => handleClose(false)}>
              <CloseIcon />
            </IconButton>
          </Grid>
          <Grid container justifyContent="center" spacing={3}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Name"
                name="name"
                value={state.name}
                onChange={handleNameChange}
                error={state.nameError ? true : false}
                helperText={state.nameError}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Description"
                name="description"
                value={state.description}
                onChange={handleDescriptionChange}
                error={state.descriptionError ? true : false}
                helperText={state.descriptionError}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Stripe Price Id"
                name="stripePriceId"
                value={state.stripePriceId}
                onChange={handleStripePriceIdChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Stripe Product Id"
                name="stripeProductId"
                value={state.stripeProductId}
                onChange={handleStripeProductIdChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                label="Price"
                name="price"
                value={state.price}
                onChange={handlePriceChange}
                error={state.priceError ? true : false}
                helperText={state.priceError}
              />
            </Grid>
            <Grid item xs={6}>
              {/* <TextField
                required
                fullWidth
                label="Currency Iso Code"
                name="currencyIsoCode"
                value={state.currencyIsoCode}
                onChange={handleCurrencyIsoCodeChange}
              /> */}
              <TextField
                required
                select
                name="currencyIsoCode"
                value={state.currencyIsoCode}
                onChange={handleCurrencyIsoCodeChange}
                size="small"
                sx={{ verticalAlign: "middle", margin: 1, minWidth: 75 }}
              >
                {currency.map((option) => (
                  <MenuItem value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Billing Interval"
                name="billingInterval"
                value={state.billingInterval}
                onChange={handleBillingIntervalChange}
                type="number"
                helperText="Enter Billing Interval in period of months, for One-Time Pay mention billing interval as 0"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            color="secondary"
            variant="contained"
            size="large"
            fullWidth
            type="submit"
            disabled={isSubmitBtnDisbaled}
            loading={submitBtnLoading}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
